import { useEffect, useState } from "react";
import { Clock, MapPin, Navigation, Phone } from "lucide-react";
import { BUSINESS } from "../data/site";
import { Reveal } from "./Reveal";

// [openHour, closeHour] per weekday, visitor's local time. Sunday = closed.
const SCHEDULE = {
  0: null,
  1: [9, 19],
  2: [9, 19],
  3: [9, 19],
  4: [9, 19],
  5: [9, 19],
  6: [9, 17],
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const fmtHour = (h) => (h > 12 ? `${h - 12} PM` : `${h} AM`);

const getOpenStatus = (now) => {
  const today = SCHEDULE[now.getDay()];
  const hour = now.getHours() + now.getMinutes() / 60;
  if (today && hour >= today[0] && hour < today[1]) {
    return { open: true, label: `Open now — closes ${fmtHour(today[1])}` };
  }
  if (today && hour < today[0]) {
    return { open: false, label: `Closed — opens ${fmtHour(today[0])}` };
  }
  for (let i = 1; i <= 7; i++) {
    const next = SCHEDULE[(now.getDay() + i) % 7];
    if (next) {
      const dayLabel = i === 1 ? "tomorrow" : DAY_NAMES[(now.getDay() + i) % 7];
      return { open: false, label: `Closed — opens ${dayLabel} ${fmtHour(next[0])}` };
    }
  }
  return { open: false, label: "Closed" };
};

const OpenNowBadge = () => {
  const [status, setStatus] = useState(() => getOpenStatus(new Date()));

  useEffect(() => {
    const timer = setInterval(() => setStatus(getOpenStatus(new Date())), 60000);
    return () => clearInterval(timer);
  }, []);

  return (
    <span
      data-testid="open-now-badge"
      className={`mt-2 inline-flex w-fit items-center gap-2 rounded-full border px-3.5 py-1.5 text-[11px] font-bold tracking-[0.12em] ${
        status.open
          ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
          : "border-white/15 bg-white/[0.05] text-white/60"
      }`}
    >
      <span className="relative flex h-2 w-2">
        {status.open && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
        )}
        <span
          className={`relative inline-flex h-2 w-2 rounded-full ${
            status.open ? "bg-emerald-400" : "bg-white/40"
          }`}
        />
      </span>
      {status.label.toUpperCase()}
    </span>
  );
};

export const LocationHours = () => (
  <section id="contact" data-testid="location-section" className="bg-[#0c1220] py-20 sm:py-28">
    <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
      <Reveal>
        <h2 className="text-4xl font-black uppercase tracking-[-0.02em] text-white sm:text-5xl lg:text-6xl">
          LOCATION &amp; HOURS
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
        <Reveal delay={0.08} className="flex flex-col">
          <div className="space-y-7">
            <div className="flex items-start gap-4">
              <span className="mt-0.5 rounded-lg bg-white/[0.06] p-3">
                <MapPin className="h-5 w-5 text-[#9fd3f0]" />
              </span>
              <div>
                <p className="text-[11px] font-bold tracking-[0.2em] text-white/40">ADDRESS</p>
                <p data-testid="location-address" className="mt-1.5 text-lg font-semibold leading-snug text-white">
                  {BUSINESS.address}
                  <br />
                  Calhoun, GA 30701
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="mt-0.5 rounded-lg bg-white/[0.06] p-3">
                <Phone className="h-5 w-5 text-[#9fd3f0]" />
              </span>
              <div>
                <p className="text-[11px] font-bold tracking-[0.2em] text-white/40">PHONE</p>
                <a
                  data-testid="location-phone-link"
                  href={`tel:${BUSINESS.phoneTel}`}
                  className="mt-1.5 block text-lg font-semibold text-white transition-colors hover:text-[#9fd3f0]"
                >
                  {BUSINESS.phoneDisplay}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="mt-0.5 rounded-lg bg-white/[0.06] p-3">
                <Clock className="h-5 w-5 text-[#9fd3f0]" />
              </span>
              <div>
                <p className="text-[11px] font-bold tracking-[0.2em] text-white/40">HOURS</p>
                <OpenNowBadge />
                {BUSINESS.hours.length === 0 ? (
                  <p data-testid="location-hours" className="mt-1.5 text-lg font-semibold text-white/70">
                    Hours coming soon.
                  </p>
                ) : (
                  <ul data-testid="location-hours" className="mt-1.5 space-y-1">
                    {BUSINESS.hours.map((h) => (
                      <li key={h.day} className="flex gap-6 text-base text-white/85">
                        <span className="w-36 font-semibold text-white">{h.day}</span>
                        <span>{h.time}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              data-testid="get-directions-button"
              href={BUSINESS.mapsDirections}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 rounded-lg bg-[#4a9bdc] px-7 py-3.5 text-[13px] font-bold tracking-[0.1em] text-white transition-colors duration-300 hover:bg-[#5aaae8]"
            >
              <Navigation className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
              GET DIRECTIONS
            </a>
            <a
              data-testid="call-the-shop-button"
              href={`tel:${BUSINESS.phoneTel}`}
              className="inline-flex items-center gap-2.5 rounded-lg border border-white/25 px-7 py-3.5 text-[13px] font-bold tracking-[0.1em] text-white transition-colors duration-300 hover:border-white/50 hover:bg-white/[0.06]"
            >
              <Phone className="h-4 w-4" />
              CALL THE SHOP
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="h-full">
          <div
            data-testid="location-map"
            className="map-frame h-full min-h-[320px] overflow-hidden rounded-xl border border-white/10"
          >
            <iframe
              title="Map to Phade Barbers — 407 Oothcalooga St, Calhoun, GA 30701"
              src={BUSINESS.mapsEmbed}
              className="h-full min-h-[320px] w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
